if (!customElements.get('lookbook-cards')) {
  class LookbookCards extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        sliderWrapper: '.lookbook-cards__items',
        nextEl: '.swiper-btn-next',
        prevEl: '.swiper-btn-prev',
      };
      this.classes = {
        swiper: 'swiper',
        swiperWrapper: 'swiper-wrapper',
      };

      this.sectionId = this.dataset.sectionId;
      this.section = this.closest('.section-' + this.sectionId);
      this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);

      this.maxWidth = parseInt(this.dataset.maxWidth) || 5120;
      this.isFaded = this.dataset.edgeStyle === 'faded';
      this.cardCountDesktop = Math.max(1, parseInt(this.dataset.cardCount) || 4);
      this.cardCountTablet = Math.max(1, parseInt(this.dataset.cardCountTablet) || 3);
      this.cardCountMobile = Math.max(1, parseInt(this.dataset.cardCountMobile) || 1);

      this.sliderInstance = null;

      // Defer init until FoxTheme globals are ready (handles race where
      // this script loads before the FoxTheme bundle).
      this.waitForFoxTheme()
        .then(() => this.initSlider())
        .catch((err) => {
          console.error('[lookbook-cards] FoxTheme not available:', err);
        });
    }

    waitForFoxTheme() {
      return new Promise((resolve, reject) => {
        const ready = () =>
          Boolean(
            window.FoxTheme &&
              window.FoxTheme.Carousel &&
              window.FoxTheme.Swiper &&
              window.FoxTheme.Swiper.Navigation &&
              window.FoxTheme.a11y &&
              typeof window.FoxTheme.a11y.getFocusableElements === 'function'
          );

        if (ready()) {
          resolve();
          return;
        }

        let attempts = 0;
        const interval = setInterval(() => {
          attempts++;
          if (ready()) {
            clearInterval(interval);
            resolve();
          } else if (attempts > 50) {
            clearInterval(interval);
            reject(new Error('FoxTheme globals not available after 5 seconds'));
          }
        }, 100);
      });
    }

    initSlider() {
      if (!this.section) {
        console.error('[lookbook-cards] section root not found for sectionId:', this.sectionId);
        return;
      }
      if (!this.sliderWrapper) {
        console.error('[lookbook-cards] slider wrapper not found');
        return;
      }

      // Buttons live INSIDE the component (siblings of .swiper-wrapper),
      // so query within `this`. testimonials puts them in the section
      // header so its query scope is `this.section` — different markup,
      // different scope.
      const nextEl = this.querySelector(this.selectors.nextEl);
      const prevEl = this.querySelector(this.selectors.prevEl);

      const config = {
        modules: [FoxTheme.Swiper.Navigation],
        slidesPerView: 'auto',
        spaceBetween: 0,
        slidesOffsetBefore: this.getFadeOffset(),
        slidesOffsetAfter: this.getFadeOffset(),
        navigation: {
          nextEl: nextEl,
          prevEl: prevEl,
        },
        // Lookbook does NOT loop — at the start, prev is disabled; at the
        // end, next is disabled. Swiper handles the disabled state by
        // adding .swiper-button-disabled which dims the affected button.
        // When all slides fit on screen, Swiper applies .swiper-button-lock
        // to both buttons; CSS hides them in that case.
        loop: false,
        threshold: 2,
        watchSlidesProgress: true,
      };

      this.classList.add(this.classes.swiper);
      this.sliderWrapper.classList.add(this.classes.swiperWrapper);

      try {
        this.sliderInstance = new window.FoxTheme.Carousel(this, config);
        this.sliderInstance.init();
      } catch (err) {
        console.error('[lookbook-cards] Swiper init failed:', err);
        return;
      }

      // Initial compute deferred to next frame so layout has settled.
      requestAnimationFrame(() => this.recompute());

      // ResizeObserver retries on layout settle and viewport changes.
      if (typeof ResizeObserver !== 'undefined') {
        let resizeTimer;
        const observer = new ResizeObserver(() => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(() => this.recompute(), 100);
        });
        observer.observe(this);
      }

      // Keyboard focus tracking: keep the focused slide in view.
      FoxTheme.a11y.getFocusableElements(this).forEach((el) => {
        el.addEventListener('focusin', () => {
          const slide = el.closest('.swiper-slide');
          if (slide && this.sliderInstance && this.sliderInstance.slider) {
            this.sliderInstance.slider.slideTo(this.sliderInstance.slider.slides.indexOf(slide));
          }
        });
      });

      // Theme editor: track block selections so the merchant sees the
      // block they're editing. Use slideTo (not slideToLoop) since this
      // slider doesn't loop — slideToLoop requires loop:true.
      if (Shopify.designMode && this.sliderInstance) {
        document.addEventListener('shopify:block:select', (event) => {
          if (event.detail.sectionId === this.sectionId) {
            const blockEl = event.target;
            const blockIndex = Number(blockEl.dataset.index);
            if (
              this.sliderInstance.slider &&
              typeof this.sliderInstance.slider.slideTo === 'function'
            ) {
              this.sliderInstance.slider.slideTo(blockIndex);
            }
          }
        });
      }
    }

    // Fade applies only when the viewport has room for both the full
    // max_width content area AND the 40px fade zones on each side.
    // Below this breakpoint, the section fills the viewport and slides
    // span the full container with no offset — there's nowhere for the
    // fade to extend into.
    getFadeOffset() {
      if (!this.isFaded) return 0;
      if (window.innerWidth < this.maxWidth + 80) return 0;
      return 40;
    }

    // Viewport-aware card count, matching the CSS media-query convention.
    // Mobile <=768, tablet 769–1024, desktop >1024.
    getCardCount() {
      const w = window.innerWidth;
      if (w <= 768) return this.cardCountMobile;
      if (w <= 1024) return this.cardCountTablet;
      return this.cardCountDesktop;
    }

    recompute() {
      const fadeOffset = this.getFadeOffset();
      const swiperWidth = this.getBoundingClientRect().width;
      // Transient zero-width during early layout; ResizeObserver will
      // retry once the element is sized. CSS fallback width keeps
      // columns correct in the meantime.
      if (!swiperWidth) return;

      // Slots butt edge-to-edge across the visible track. Each slot's
      // own padding-inline (= card_inset) creates the visible inset
      // inside the slot.
      const trackWidth = swiperWidth - 2 * fadeOffset;
      const slotWidth = trackWidth / this.getCardCount();
      this.style.setProperty('--card-width-actual', slotWidth + 'px');

      // Update Swiper config when the fade offset changes (e.g. viewport
      // crossing the fade breakpoint).
      if (this.sliderInstance && this.sliderInstance.slider) {
        const slider = this.sliderInstance.slider;
        if (slider.params.slidesOffsetBefore !== fadeOffset) {
          slider.params.slidesOffsetBefore = fadeOffset;
          slider.params.slidesOffsetAfter = fadeOffset;
        }
        slider.update();
      }
    }
  }

  customElements.define('lookbook-cards', LookbookCards);
}
